/**
 * Software queries and mutations for TanStack Query (Vendor App)
 *
 * This module provides all the necessary queries and mutations for software version management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet, queryPatch, queryPost } from '../utils/request'

// Type definitions
export interface Software {
  id: number
  name: string
  description?: string
  is_active: boolean
}

export interface SoftwareVersion {
  id: number
  software_id: number
  software_name?: string
  version_code: number
  version_name: string
  size_bytes: number
  sha256_hash: string
  is_active: boolean
  created_at: string
  updated_at?: string
}

export interface SoftwareListParams {
  active_only?: boolean
}

export interface SoftwareVersionListParams {
  software_id?: number
  active_only?: boolean
  page?: number
  per_page?: number
}

/**
 * Uploading a version is a three-step flow, because packages are large enough (60MB+) that
 * streaming them through the API times out at the edge proxy:
 *
 *   1. ask the API to presign an S3 PUT       -> useCreateUploadUrlMutation
 *   2. PUT the bytes straight to S3           -> uploadVersionFile
 *   3. ask the API to register the object     -> useFinalizeVersionMutation
 *
 * Step 2 never touches our API. Steps 1 and 3 must be given the same software_id/version_code:
 * the storage key is derived from them, so a mismatch makes step 3 fail as "file not found".
 */
export interface UploadUrlParams {
  software_id: number
  version_code: number
}

export interface UploadUrlResponse {
  upload_url: string // presigned S3 PUT URL
  s3_key: string // informational only — step 3 re-derives the key server-side
  expires_in: number // seconds
}

export interface FinalizeVersionParams {
  software_id: number
  version_code: number
  version_name: string
  filename: string // original file name, stored as metadata for the download attachment
  content_type?: string
}

/**
 * Response of the finalize step. Narrower than SoftwareVersion: the API computes sha256_hash and
 * size_bytes server-side by streaming the object back from S3, and returns no lifecycle fields.
 */
export interface SoftwareVersionUploadResponse {
  id: number
  software_id: number
  software_name: string
  version_code: number
  version_name: string
  sha256_hash: string
  size_bytes: number
}

export interface SoftwareVersionActiveUpdate {
  is_active: boolean // false = yank, true = restore
}

export interface SoftwareVersionDownloadResponse {
  url: string // presigned S3 URL, valid for 15 minutes
  filename: string // original upload filename, used for the download attachment
}

export interface UpdateVersionActiveParams {
  version_id: number
  is_active: boolean
}

// Cache configuration
const CACHE_TIMES = {
  SOFTWARES: 10 * 60 * 1000, // 10 minutes
  VERSIONS: 2 * 60 * 1000, // 2 minutes
}

/**
 * QUERIES
 */

/**
 * Query hook to fetch list of softwares
 */
export const useSoftwaresQuery = (params: MaybeRefOrGetter<SoftwareListParams> = { active_only: true }) => {
  return useQuery({
    queryKey: computed(() => [...queryKeys.software(), 'list', toValue(params)]),
    queryFn: async (): Promise<Software[]> => {
      const queryParams = toValue(params)
      return queryGet<Software[]>('/v2/admin/softwares', { params: queryParams })
    },
    staleTime: CACHE_TIMES.SOFTWARES,
  })
}

/**
 * Query hook to fetch software versions
 */
export const useSoftwareVersionsQuery = (params: MaybeRefOrGetter<SoftwareVersionListParams>) => {
  return useQuery({
    queryKey: computed(() => queryKeys.softwareList(toValue(params))),
    queryFn: async (): Promise<SoftwareVersion[]> => {
      const queryParams = { active_only: true, ...toValue(params) }
      return queryGet<SoftwareVersion[]>('/v2/admin/software-versions', { params: queryParams })
    },
    staleTime: CACHE_TIMES.VERSIONS,
    enabled: computed(() => !!toValue(params)?.software_id),
  })
}

/**
 * MUTATIONS
 */

/**
 * Content type the PUT URL is signed with. S3 folds it into the signature, so sending any other
 * value — including letting the browser infer one from the File — is rejected as
 * SignatureDoesNotMatch. It must be set explicitly and must match exactly.
 */
export const UPLOAD_CONTENT_TYPE = 'application/octet-stream'

/**
 * Step 1: reserve a slot and get a presigned PUT URL.
 *
 * This is where an unknown software (400) or an already-taken version code (409) surfaces, before
 * a single byte moves — so the caller can report the problem without making the user wait.
 */
export const useCreateUploadUrlMutation = () => {
  return useMutation({
    mutationFn: async (params: UploadUrlParams): Promise<UploadUrlResponse> => {
      return queryPost<UploadUrlResponse>('/v2/admin/software-versions/upload-url', params)
    },
  })
}

/**
 * Step 2: send the bytes straight to S3.
 *
 * Deliberately raw XHR instead of the shared axios instance: that instance prefixes /api and
 * attaches auth headers, both of which would break the presigned request. XHR (not fetch) because
 * it is the only way to observe upload progress on a request body.
 */
export interface UploadProgress {
  percent: number
  loaded: number // bytes sent so far
  total: number // 0 when the browser cannot determine the body length
}

export const uploadVersionFile = (
  uploadUrl: string,
  file: File,
  onProgress?: (progress: UploadProgress) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', UPLOAD_CONTENT_TYPE)

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress?.({
          percent: Math.round((event.loaded / event.total) * 100),
          loaded: event.loaded,
          total: event.total,
        })
      }
    })
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`o armazenamento respondeu ${xhr.status}`))
      }
    })
    xhr.addEventListener('error', () => reject(new Error('falha de rede')))
    xhr.addEventListener('timeout', () => reject(new Error('tempo limite excedido')))

    // The raw File, not FormData — a multipart wrapper would corrupt the stored object.
    xhr.send(file)
  })
}

/**
 * Step 3: register the uploaded object as a version.
 *
 * The API streams the object back from S3 to compute the hash and size, so this can take a while
 * for a large package even though the request body is tiny.
 */
export const useFinalizeVersionMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: FinalizeVersionParams): Promise<SoftwareVersionUploadResponse> => {
      return queryPost<SoftwareVersionUploadResponse>('/v2/admin/software-versions', params)
    },
    onSuccess: () => {
      // Invalidate the whole software namespace: list keys carry the full filter object
      // (active_only/page/per_page), so a narrower key would not match the mounted query.
      queryClient.invalidateQueries({ queryKey: queryKeys.software() })
    },
  })
}

/**
 * Mutation hook to yank (is_active: false) or restore (is_active: true) a software version.
 *
 * Yanking the current latest version automatically rolls devices back to the previous active
 * version, so any version list and "latest version" indicator must be refreshed afterwards.
 */
export const useUpdateVersionActiveMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: UpdateVersionActiveParams): Promise<SoftwareVersion> => {
      const body: SoftwareVersionActiveUpdate = { is_active: params.is_active }
      return queryPatch<SoftwareVersion>(`/v2/admin/software-versions/${params.version_id}`, body)
    },
    onSuccess: () => {
      // The device-facing "latest" can change server-side, so refresh every software version list.
      queryClient.invalidateQueries({ queryKey: queryKeys.software() })
    },
  })
}

/**
 * Mutation hook to fetch a short-lived presigned download URL for a software version's binary.
 *
 * Modelled as a mutation so it runs imperatively per click — the URL expires in 15 minutes and
 * must not be cached, so a fresh one is requested for every download.
 */
export const useDownloadVersionMutation = () => {
  return useMutation({
    mutationFn: async (versionId: number): Promise<SoftwareVersionDownloadResponse> => {
      return queryGet<SoftwareVersionDownloadResponse>(
        `/v2/admin/software-versions/${versionId}/download-url`
      )
    },
  })
}

/**
 * Hook that combines software queries and mutations for convenient usage
 */
export const useSoftwareOperations = () => {
  return {
    // Queries
    useSoftwaresQuery,
    useSoftwareVersionsQuery,

    // Mutations
    useCreateUploadUrlMutation,
    useFinalizeVersionMutation,
    useUpdateVersionActiveMutation,
    useDownloadVersionMutation,
  }
}
